import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { GlobalContext } from 'providers/GlobalProvider';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Modal,
  styled,
  Tooltip,
} from '@mui/material';
import { useBulkUploadEvents } from 'hooks/useSocketIO';
import { enqueueSnackbar } from 'notistack';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DatabaseResultsReportModal from '../Modals/DatabaseResultsReportModal';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

// Icons
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined';
import { format } from 'date-fns';

// API Hooks
import {
  useCreateBulkEntitiesWithoutDuplicateCheck,
  useDeclinePreviousStatus,
  useGetEntitiesByDatabaseId,
  useRemoveDatabaseEntities,
  useRestorePreviousStatus,
  useRestorePreviousStatusByFlag,
  useUpdateDatabaseEntitiesStatus,
} from 'api/bitedb/entities/entities.hooks';
import { isStatusFlag } from 'api/bitedb/entities/statusFlags';

import {
  useDeleteBulkProducts,
  useGetProductsByDatabaseId,
  useUpdateProductStatus,
} from 'api/bitedb/products/products.hooks';

import {
  useDeleteBulkTariffs,
  useGetTariffsByDatabaseId,
  useUpdateTariffStatus,
} from 'api/bitedb/tariffs/tariffs.hooks';
import {
  downloadTariffCsv,
  getAllTariffsByDatabaseId,
} from 'api/bitedb/tariffs/tariffs.service';
import { getProductsByDatabaseId } from 'api/bitedb/products/products.service';
import {
  databaseKeys,
  useDeleteDatabase,
} from 'api/bitedb/database/database.hooks';

import {
  screeningKeys,
  useGetDatabaseScreeningStatus,
  useGetScreeningRunsByDatabaseId,
  useRunEntityServiceDatabaseScreening,
  useRunRetryScreening,
  useRunTariffCheck,
} from 'api/bitedb/screeningHistory';
import { useQueryClient } from '@tanstack/react-query';
import { DELAYED_SCREENING_MESSAGE } from '../NewComponents/DatabaseCard';

import {
  STATS_QUERY_KEYS,
  useGetStatsByDatabaseId,
} from 'api/bitedb/stats/stats.hooks';
import {
  getResultsReportDownloadUrl,
  listResultsReportsForDatabase,
  ResultsReportRow,
} from 'api/bitedb/resultsReport';

import {
  auditKeys,
  useGetAuditLogActionsByDatabaseId,
  useGetAuditLogsByDatabaseIdPaginated,
} from 'api/bitedb/audit/audit.hooks';
import {
  EMPTY_AUDIT_LOG_DATABASE_FILTERS,
  hasActiveAuditLogFilters,
  IAuditLogDatabaseFilters,
} from 'api/bitedb/audit/audit.d';

// Components
import MasterTable from '../Tables/MasterTable';
import Columns from '../Tables/Columns';
import Row from '../Tables/Row';
import ActivityHistoryColumns from '../Tables/ActivityHistoryColumns';
import ActivityHistoryRow from '../Tables/ActivityHistoryRow';
import GeneratedReportsColumns from '../Tables/GeneratedReportsColumns';
import GeneratedReportsRow from '../Tables/GeneratedReportsRow';
import ScreeningSettings from './ScreeningSettings';
import TariffSettings from './TariffSettings';
import ScreeningFailureBanner from './ScreeningFailureBanner';
import StatusResetBanner from './StatusResetBanner';
import RestorePreviousByFlagButton from '../common/RestorePreviousByFlagButton';
import { BulkUploadModal } from '../Modal/BulkUploadModal';
import BulkExportModal from '../Modals/BulkExportModal';
import EntityChangesReportModal from '../Modals/EntityChangesReportModal';
import BulkLinkEntitiesModal from '../Modal/BulkLinkEntitiesModal';
import { AddEntityModal } from '../Modal/AddEntityModal';
import { AddSingleTariffModal } from '../Modal/AddSingleTariffModal';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';
import ChangeStatus from '../Modal/ChangeStatus';
import ConfirmStatusChangeModal from '../Modal/ConfirmStatusChangeModal';
import {
  DuplicateEntity,
  PaginationParams,
} from 'api/bitedb/entities/entities';
import { DuplicateReviewContent } from './DuplicateReviewContent';
import { BulkStatusTransitionModal } from '../Modal/BulkStatusTransitionModal';
import TariffCols from '../Tables/TariffCols';
import ProductColumns from '../Tables/ProductColumns';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import DatabaseFilterModal from '../Modal/DatabaseFilterModal';
import AuditLogFilterModal from '../Modal/AuditLogFilterModal';
import { checkProductCredits } from 'utils/productCredits';
import InsufficientCredits from 'pages/credit-plans/InsufficientCredits';
import AIReportBuilder from 'pages/reports/AIReportBuilder';
import { formatPeopleCompaniesDatabaseMeta } from './peopleCompaniesDatabaseMeta';

const STATUS_RESET_PREVIOUS_FLAGS = ['quarantined', 'barred', 'cleared'] as const;

// Helper Hook for Debouncing
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const EntitiesType = 'people & companies';
const HISTORY_TAB = 'Screening History';
// Tabs that show the item list; other tabs (history, settings, reports) don't.
const LIST_TABS = ['All', 'Review Queue', 'Quarantined', 'Barred', 'Cleared'];
const HISTORY_ITEMS_PER_PAGE = 10;

// Timers check this so nothing polls from a hidden browser tab.
const isDocumentHidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';
const GENERATED_REPORTS_ITEMS_PER_PAGE = 10;

const fetchLocation = (param: string) => {
  switch (param.toLowerCase()) {
    case 'people & companies':
      return '/supplier';
    case 'tariffs':
      return '/tariff';
    case 'products':
      return '/product';
    default:
      return '/supplier';
  }
};

interface DashboardProps {
  database: any;
  isLoading: boolean;
}

const CustomSelect = styled(Select)({
  '.MuiSelect-icon': {
    color: 'var(--text-classify)',
  },
  overflow: 'hidden',
  color: 'var(--Text-Main, var(--text-primary))',
  textOverflow: 'ellipsis',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  borderRadius: '8px',
  border: '1px solid #2E3036',
});

const parseListParam = (params: URLSearchParams, key: string) => {
  const rawValues = params.getAll(key);
  if (!rawValues.length) {
    return [];
  }

  return rawValues
    .flatMap((entry) =>
      decodeURIComponent(entry)
        .split(',')
        .map((item) => item.trim()),
    )
    .filter((item) => Boolean(item));
};

const parseYesNoParam = (value: string | null): 'yes' | 'no' | null =>
  value === 'yes' || value === 'no' ? value : null;

const DatabaseInfo: React.FC<DashboardProps> = ({ database }) => {
  //Product updates
  const [, setEditProductCatelogModalOpen] = useState(false);
  const [, setEditProductItem] = useState({});

  const [productTab, setProductTab] = useState<string>('Products');
  const [isResultsReportModalOpen, setIsResultsReportModalOpen] =
    useState(false);

  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [bannerDismissedForRetry, setBannerDismissedForRetry] = useState(false);
  // State Management from URL
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const page = parseInt(queryParams.get('page') || '1', 10);
  const limit = parseInt(queryParams.get('limit') || '10', 10);
  const sortBy = queryParams.get('sortBy') || '';
  const sortOrder = (queryParams.get('sortOrder') as 'asc' | 'desc') || '';
  const searchTerm = queryParams.get('search') || '';
  const hierarchyTypeTerm = queryParams.get('hierarchyType') || 'Assembly';
  const tab = queryParams.get('tab') || 'All';
  const statusFiltersFromQuery = useMemo(
    () => parseListParam(queryParams, 'statusFilters'),
    [queryParams],
  );
  const riskFiltersFromQuery = useMemo(
    () => parseListParam(queryParams, 'riskFilters'),
    [queryParams],
  );
  const hasNewResultsFromQuery = useMemo(
    () => parseYesNoParam(queryParams.get('hasNewResults')),
    [queryParams],
  );
  const statusResetFromQuery = queryParams.get('statusReset') === 'true';

  const parseFilterValues = (values: string[]): string[] =>
    values
      .flatMap((entry) => entry.split(','))
      .map((entry) => entry.trim())
      .filter(Boolean);

  const parsedStatusFilters = useMemo(
    () => parseFilterValues(queryParams.getAll('statusFilters')),
    [queryParams],
  );
  const parsedRiskFilters = useMemo(
    () => parseFilterValues(queryParams.getAll('riskFilters')),
    [queryParams],
  );
  const parsedLensFilters = useMemo(
    () => parseFilterValues(queryParams.getAll('lensFilters')),
    [queryParams],
  );
  const parsedHitCountRanges = useMemo(
    () => parseFilterValues(queryParams.getAll('hitCountRanges')),
    [queryParams],
  );
  const parsedNameStartsWith = useMemo(() => {
    const rawValues = parseFilterValues(queryParams.getAll('nameStartsWith'));
    const normalized = rawValues
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => value[0]?.toUpperCase())
      .filter(
        (value): value is string => Boolean(value) && /^[A-Z]$/.test(value),
      );
    return Array.from(new Set(normalized));
  }, [queryParams]);
  const parsedHasNewResults = useMemo<'yes' | 'no' | null>(() => {
    const value = queryParams.get('hasNewResults');
    if (
      value &&
      (value.toLowerCase() === 'yes' || value.toLowerCase() === 'no')
    ) {
      return value.toLowerCase() as 'yes' | 'no';
    }
    return null;
  }, [queryParams]);
  const parsedImpactedByError = useMemo<'yes' | 'no' | undefined>(() => {
    const value = queryParams.get('impactedByError');
    if (
      value &&
      (value.toLowerCase() === 'yes' || value.toLowerCase() === 'no')
    ) {
      return value.toLowerCase() as 'yes' | 'no';
    }
    return undefined;
  }, [queryParams]);

  const { data: stats } = useGetStatsByDatabaseId(database?.id ?? '-1');

  const [statusResetBannerDismissedCount, setStatusResetBannerDismissedCount] =
    useState(0);

  useEffect(() => {
    setStatusResetBannerDismissedCount(0);
  }, [database?.id]);

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [localHierarchyType, setLocalHierarchyType] =
    useState(hierarchyTypeTerm);
  const debouncedSearchTerm = useDebounce(localSearchTerm, 500);
  const debouncedHierarchyTypeTerm = useDebounce(localHierarchyType, 500);

  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showBulkLinkEntitiesModal, setShowBulkLinkEntitiesModal] =
    useState(false);
  const [, setShowAddProductModal] = useState(false);
  const [showBulkStatusTransitionModal, setShowBulkStatusTransitionModal] =
    useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showAddTariffModal, setShowAddTariffModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingItems, setIsDeletingItems] = useState(false);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [requiredDatabaseCredits, setRequiredDatabaseCredits] = useState(1);
  const [statusFilters, setStatusFilters] =
    useState<string[]>(parsedStatusFilters);
  const [riskFilters, setRiskFilters] = useState<string[]>(parsedRiskFilters);
  const [lensFilters, setLensFilters] = useState<string[]>(parsedLensFilters);
  const [hitCountRanges, setHitCountRanges] =
    useState<string[]>(parsedHitCountRanges);
  const [nameStartsWith, setNameStartsWith] =
    useState<string[]>(parsedNameStartsWith);
  const [hasNewResultsFilter, setHasNewResultsFilter] = useState<
    'yes' | 'no' | null
  >(parsedHasNewResults);
  const [historyPage, setHistoryPage] = useState(1);
  const historyOffset = (historyPage - 1) * HISTORY_ITEMS_PER_PAGE;
  const [auditFilters, setAuditFilters] = useState<IAuditLogDatabaseFilters>(
    EMPTY_AUDIT_LOG_DATABASE_FILTERS,
  );
  const [auditSearchInput, setAuditSearchInput] = useState('');
  const debouncedAuditSearch = useDebounce(auditSearchInput, 500);
  const [showAuditFilterModal, setShowAuditFilterModal] = useState(false);
  const auditFiltersWithSearch: IAuditLogDatabaseFilters = {
    ...auditFilters,
    search: debouncedAuditSearch || undefined,
  };
  const hasActiveAuditFilters = hasActiveAuditLogFilters(auditFiltersWithSearch);
  const isHistoryTab = tab === HISTORY_TAB;
  const { data: auditFilterActions = [] } = useGetAuditLogActionsByDatabaseId(
    database?.id || '',
    cur_user?.selected_team?.id,
    { enabled: isHistoryTab },
  );
  const [isScreeningCurrently, setIsScreeningCurrently] = useState(false);
  const [duplicateReviewState, setDuplicateReviewState] = useState<{
    open: boolean;
    duplicates: DuplicateEntity[];
    fallbackEntities: any[];
  }>({
    open: false,
    duplicates: [],
    fallbackEntities: [],
  });
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [resultsReports, setResultsReports] = useState<ResultsReportRow[]>([]);
  const [isLoadingResultsReports, setIsLoadingResultsReports] = useState(false);
  const [isRefreshingResultsReports, setIsRefreshingResultsReports] =
    useState(false);
  const [isDownloadingReportJobId, setIsDownloadingReportJobId] = useState<
    string | null
  >(null);
  const [generatedReportsPage, setGeneratedReportsPage] = useState(1);
  const [generatedReportsTotalPages, setGeneratedReportsTotalPages] =
    useState(1);
  const [expandedEntityIds, setExpandedEntityIds] = useState<
    Record<string, boolean>
  >({});
  const [isAwaitingAiPhase, setIsAwaitingAiPhase] = useState(false);
  const aiPhaseDeadlineRef = useRef<number | null>(null);

  const [screeningCheckActive, setScreeningCheckActive] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isChangesReportModalOpen, setIsChangesReportModalOpen] = useState(false);
  const [exportFileName, setExportFileName] = useState('');
  const [exportFormat, setExportFormat] = useState('BITE_DEFAULT');
  const [isExporting, setIsExporting] = useState(false);

  const handleAddItemClick = useCallback(async () => {
    if (isScreeningCurrently) {
      return;
    }

    const teamId = cur_user?.selected_team?.id;
    if (!teamId) {
      enqueueSnackbar('Unable to validate team credits.', { variant: 'error' });
      return;
    }

    const addItemCreditCost = 1;
    setRequiredDatabaseCredits(addItemCreditCost);

    try {
      // Entity Hub draws on the same combined Database Records pool as
      // Import/Export Hub (db_size + product_db_size, usage summed across
      // all three hubs) -- see checkProductCredits for why.
      const creditCheck = await checkProductCredits(teamId, addItemCreditCost);

      if (!creditCheck.ok) {
        setShowInsufficientCredits(true);
        return;
      }

      if (database.type === 'tariffs') {
        setShowAddTariffModal(true);
      } else if (database.type === 'products') {
        setShowAddProductModal(true);
      } else {
        setShowAddItemModal(true);
      }
    } catch (error) {
      console.error(
        'Failed to validate credits before opening add item',
        error,
      );
      enqueueSnackbar('Unable to validate team credits.', { variant: 'error' });
    }
  }, [cur_user?.selected_team?.id, database.type, isScreeningCurrently]);

  const handleImportDataClick = useCallback(() => {
    if (isScreeningCurrently || isBulkUploading) {
      return;
    }
    setShowBulkUploadModal(true);
  }, [isBulkUploading, isScreeningCurrently]);

  // Live screening status: entity databases poll the entity service (Redis-backed
  // progress); other database types stay on automations.
  const { data: screeningStatus, refetch: refetchScreeningStatus } =
    useGetDatabaseScreeningStatus(database?.id, database?.type === EntitiesType);

  useEffect(() => {
    setStatusFilters(statusFiltersFromQuery);
  }, [statusFiltersFromQuery]);

  useEffect(() => {
    setRiskFilters(riskFiltersFromQuery);
  }, [riskFiltersFromQuery]);

  useEffect(() => {
    setHasNewResultsFilter(hasNewResultsFromQuery);
  }, [hasNewResultsFromQuery]);

  // Listen for bulk upload events for this database
  useBulkUploadEvents(undefined, {
    onComplete: (result) => {
      if (result.databaseId !== database?.id?.toString()) {
        return;
      }
      setIsBulkUploading(false);
      refetchItems();

      const duplicateCount = Array.isArray(result.duplicates)
        ? result.duplicates.length
        : 0;
      if (duplicateCount > 0) {
        enqueueSnackbar(
          `${duplicateCount} potential duplicate${
            duplicateCount > 1 ? 's' : ''
          } need review in the upload modal.`,
          { variant: 'warning' },
        );
      }

      enqueueSnackbar(
        `Upload complete! ${
          result.duplicatesRemoved
            ? `${result.duplicatesRemoved} duplicates were removed.`
            : ''
        }`,
        { variant: 'success' },
      );
    },
    onFailed: (result) => {
      if (result.databaseId === database?.id?.toString()) {
        console.error('[DatabaseInfo] Bulk upload failed:', result);
        setIsBulkUploading(false);
        enqueueSnackbar(
          result.error ||
            'Upload failed. Please check your data and try again.',
          {
            variant: 'error',
          },
        );
      }
    },
  });

  const handleUrlChange = useCallback(
    (newParams: Record<string, string | number>) => {
      const updatedParams = new URLSearchParams(location.search);
      Object.entries(newParams).forEach(([key, value]) => {
        if (value) {
          updatedParams.set(key, String(value));
        } else {
          updatedParams.delete(key);
        }
      });
      history.replace({ search: updatedParams.toString() });
    },
    [history, location.search],
  );

  const requestScreeningCheck = useCallback(() => {
    refetchScreeningStatus();
    setScreeningCheckActive(true);
  }, [refetchScreeningStatus]);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Write the search only when the typed term settles. Reacting to URL changes
  // as well would write the stale input back over a search set elsewhere
  // (back/forward, links), costing a second list request.
  const lastDebouncedSearchRef = useRef(debouncedSearchTerm);
  useEffect(() => {
    if (debouncedSearchTerm === lastDebouncedSearchRef.current) {
      return;
    }
    lastDebouncedSearchRef.current = debouncedSearchTerm;
    if (debouncedSearchTerm !== searchTerm) {
      handleUrlChange({ search: debouncedSearchTerm, page: 1 });
    }
  }, [debouncedSearchTerm, searchTerm, handleUrlChange]);

  useEffect(() => {
    if (debouncedHierarchyTypeTerm !== hierarchyTypeTerm) {
      handleUrlChange({ hierarchyType: debouncedHierarchyTypeTerm, page: 1 });
    }
  }, [debouncedHierarchyTypeTerm, hierarchyTypeTerm, handleUrlChange]);

  useEffect(() => {
    setStatusFilters(parsedStatusFilters);
  }, [parsedStatusFilters]);

  useEffect(() => {
    setRiskFilters(parsedRiskFilters);
  }, [parsedRiskFilters]);

  useEffect(() => {
    setLensFilters(parsedLensFilters);
  }, [parsedLensFilters]);

  useEffect(() => {
    setHitCountRanges(parsedHitCountRanges);
  }, [parsedHitCountRanges]);

  useEffect(() => {
    setNameStartsWith(parsedNameStartsWith);
  }, [parsedNameStartsWith]);

  useEffect(() => {
    setHasNewResultsFilter(parsedHasNewResults);
  }, [parsedHasNewResults]);

  useEffect(() => {
    if (!screeningCheckActive) {
      return undefined;
    }

    const interval = setInterval(() => {
      if (isDocumentHidden()) {
        return;
      }
      refetchScreeningStatus();
    }, 5000);

    const timeout = setTimeout(() => {
      setScreeningCheckActive(false);
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [screeningCheckActive, refetchScreeningStatus]);

  useEffect(() => {
    if (screeningStatus?.inProgress && screeningCheckActive) {
      setScreeningCheckActive(false);
    }
  }, [screeningStatus?.inProgress, screeningCheckActive]);

  // The list reads every parameter from the URL, not from the filter state
  // (which follows the URL one render later), so one URL change re-keys the
  // list once.
  const paginationParams: PaginationParams = {
    page: page,
    limit: limit,
    sortBy: sortBy,
    sortOrder: sortOrder,
    tab: tab,
    statusFilters: parsedStatusFilters,
    riskFilters: parsedRiskFilters,
    hasNewResults: parsedHasNewResults || undefined,
    statusReset: statusResetFromQuery || undefined,
    lensFilters: parsedLensFilters,
    hitCountRanges: parsedHitCountRanges,
    nameStartsWith: parsedNameStartsWith,
    impactedByError: parsedImpactedByError,
  };

  const hasActiveFilters =
    parsedStatusFilters.length > 0 ||
    parsedRiskFilters.length > 0 ||
    Boolean(parsedHasNewResults) ||
    parsedLensFilters.length > 0 ||
    parsedHitCountRanges.length > 0 ||
    parsedNameStartsWith.length > 0 ||
    Boolean(parsedImpactedByError) ||
    statusResetFromQuery;

  // The search also comes from the URL, not the debounced input: the debounce
  // writes `search` and `page=1` in one replace, so a search typed on page 2+
  // fetches once rather than on the old page and then on page 1.
  //
  // On a tab that doesn't show the list (history, settings, reports) the list
  // keeps the query the user last saw, so opening those tabs sends no list
  // request and the header count stays put. A view opened straight onto such
  // a tab loads the 'All' list once, for that count.
  const isListTab = LIST_TABS.includes(tab);
  const currentListQuery = {
    searchTerm: searchTerm,
    hierarchyTypeTerm: hierarchyTypeTerm,
    paginationParams: paginationParams,
  };
  const lastListQueryRef = useRef<typeof currentListQuery | null>(null);
  if (isListTab) {
    lastListQueryRef.current = currentListQuery;
  }
  const listQuery = isListTab
    ? currentListQuery
    : lastListQueryRef.current ?? {
        ...currentListQuery,
        paginationParams: { ...paginationParams, tab: 'All', page: 1 },
      };

  const {
    data: tableData,
    pagination: paginationData,
    isLoading: isLoadingData,
    error: itemsError,
    refetch: refetchItems,
  } = database.type === EntitiesType
    ? useGetEntitiesByDatabaseId(
        database?.id,
        listQuery.searchTerm,
        listQuery.paginationParams,
      )
    : database.type === 'products'
    ? useGetProductsByDatabaseId(
        database?.id,
        listQuery.searchTerm,
        listQuery.hierarchyTypeTerm,
        listQuery.paginationParams,
      )
    : useGetTariffsByDatabaseId(
        database?.id,
        listQuery.searchTerm,
        listQuery.paginationParams,
      );

  const statusResetCountFromRows = useMemo(() => {
    if (!Array.isArray(tableData)) {
      return 0;
    }
    return tableData.filter((row: any) => Boolean(row?.previous_status_flag))
      .length;
  }, [tableData]);

  const statusResetCount = Math.max(
    Number(stats?.total_status_reset_entities || 0),
    statusResetCountFromRows,
  );
  const previousStatusRestoreFlags = useMemo(
    () =>
      STATUS_RESET_PREVIOUS_FLAGS.filter((flag) => {
        if (flag === 'quarantined') {
          return Number(stats?.total_status_reset_quarantined_entities || 0) > 0;
        }
        if (flag === 'barred') {
          return Number(stats?.total_status_reset_barred_entities || 0) > 0;
        }
        return Number(stats?.total_status_reset_cleared_entities || 0) > 0;
      }),
    [stats],
  );
  const showStatusResetBanner =
    database?.type === EntitiesType &&
    statusResetCount > 0 &&
    statusResetCount > statusResetBannerDismissedCount;

  useEffect(() => {
    setExpandedEntityIds({});
  }, [database?.id, searchTerm, tab]);

  const isEntityHierarchyContextMode =
    database.type === EntitiesType &&
    (Boolean(searchTerm) || hasActiveFilters);
  const isEntityStatusFlatMode =
    database.type === EntitiesType &&
    !Boolean(searchTerm) &&
    !hasActiveFilters &&
    tab.toLowerCase() !== 'all';

  // In filtered/status-tab mode for entities, group matching child rows under visible parents.
  const processedTableData = useMemo(() => {
    if (
      !isEntityHierarchyContextMode ||
      !tableData ||
      database.type !== EntitiesType
    ) {
      return tableData || [];
    }

    const entities = tableData as any[];
    const childrenByParent: Record<string, any[]> = {};
    const topLevelEntityIds = new Set<string>();

    for (const entity of entities) {
      const entityId = entity.entity_id || entity.id;
      if (entity.is_top_level || !entity.parent_entity_id) {
        if (entityId) {
          topLevelEntityIds.add(String(entityId));
        }
        continue;
      }

      const parentId = String(entity.parent_entity_id);
      if (!childrenByParent[parentId]) {
        childrenByParent[parentId] = [];
      }
      childrenByParent[parentId].push(entity);
    }

    if (Object.keys(childrenByParent).length === 0) {
      return tableData;
    }

    const result: any[] = [];
    const addedEntityIds = new Set<string>();

    for (const entity of entities) {
      const entityId = entity.entity_id || entity.id;
      const normalizedEntityId = entityId ? String(entityId) : '';
      if (!normalizedEntityId || addedEntityIds.has(normalizedEntityId)) {
        continue;
      }

      const isTopLevel = entity.is_top_level || !entity.parent_entity_id;
      if (isTopLevel) {
        const matchedChildren = childrenByParent[normalizedEntityId];
        if (matchedChildren?.length) {
          result.push({
            ...entity,
            _searchMatchedChildren: matchedChildren,
          });
        } else {
          result.push(entity);
        }
        addedEntityIds.add(normalizedEntityId);
        continue;
      }

      const parentId = entity.parent_entity_id
        ? String(entity.parent_entity_id)
        : '';

      // If parent exists on this page, child is already shown under parent row.
      if (parentId && topLevelEntityIds.has(parentId)) {
        addedEntityIds.add(normalizedEntityId);
        continue;
      }

      // If parent is not on this page, render the child directly.
      result.push(entity);
      addedEntityIds.add(normalizedEntityId);
    }

    return result;
  }, [isEntityHierarchyContextMode, tableData, database.type]);

  const displayedRowsForSelection = tableData || [];

  const toggleEntityExpand = useCallback((entityId: string) => {
    if (!entityId) return;
    setExpandedEntityIds((prev) => ({ ...prev, [entityId]: !prev[entityId] }));
  }, []);

  const {
    data: screeningRunsData,
    isLoading: isScreeningHistoryLoading,
    error: screeningHistoryError,
  } = useGetScreeningRunsByDatabaseId(database?.id, {
    // Runs back the history tab and, while a run is live without progress
    // from the status endpoint, the progress bar; nothing else reads them.
    enabled: isHistoryTab || (isScreeningCurrently && !screeningStatus?.run),
  });

  const {
    data: paginatedAuditLogsData,
    isLoading: isAuditLogsLoading,
    error: auditLogsError,
  } = useGetAuditLogsByDatabaseIdPaginated(
    database?.id || '',
    HISTORY_ITEMS_PER_PAGE,
    historyOffset,
    true, // includeErrors: show failure audit logs alongside normal activity
    auditFiltersWithSearch,
    { enabled: isHistoryTab },
  );
  const auditLogsData = paginatedAuditLogsData?.data || [];

  // Derived State and Data
  const screeningHistoryData = useMemo(() => {
    if (!screeningRunsData) {
      return [];
    }
    return [...screeningRunsData]
      .sort(
        (a: any, b: any) =>
          new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
      )
      .map((run: any) => ({
        ...run,
        type: 'screening',
        database_name: database?.name,
        screening_timestamp: run.completed_at || run.started_at,
      }));
  }, [screeningRunsData, database?.name]);

  // Process audit logs data
  const auditHistoryData = useMemo(() => {
    if (!auditLogsData) {
      return [];
    }
    return auditLogsData.map((log: any) => ({
      ...log,
      type: 'audit',
      database_name: database?.name,
      screening_timestamp: log.updated_at,
      status: 'completed', // Audit logs are always completed
      user_name: log.user_name,
      action: log.message,
    }));
  }, [auditLogsData, database?.name]);

  // Combined history data (screening + audit logs)
  const combinedHistoryData = useMemo(() => {
    if (!auditHistoryData || auditHistoryData.length <= 0) {
      return [];
    }

    return auditHistoryData.sort(
      (a: any, b: any) =>
        new Date(b.screening_timestamp).getTime() -
        new Date(a.screening_timestamp).getTime(),
    );
  }, [auditHistoryData]);

  // Derive AI in-progress status from screening status.
  // Keep a short grace window after the main screening phase finishes so
  // rows can show "Processing..." immediately while AI evaluation spins up.
  // Entity screening reports its post stage (AI + verification) explicitly via
  // verificationInProgress, so it must NOT use the inferred "awaiting AI" grace
  // window: that window only clears when aiInProgress/aiStatus change, which the
  // entity flow never emits, so it would otherwise stick the AI pill on forever.
  const isEntityDatabase = database?.type === EntitiesType;
  const isAiInProgress =
    screeningStatus?.aiInProgress === true ||
    (!isEntityDatabase && isAwaitingAiPhase);
  const verificationPhase = screeningStatus?.verificationPhase ?? null;
  const isVerificationInProgress =
    screeningStatus?.verificationInProgress === true ||
    (Boolean(verificationPhase) &&
      screeningStatus?.verificationStatus !== 'complete');
  const verificationPhaseLabel =
    verificationPhase === 'apivoid'
      ? 'Enriching domain, email & phone'
      : verificationPhase === 'verification'
      ? 'Verifying address, website & registries'
      : verificationPhase === 'adverse_media'
      ? 'Checking adverse media'
      : 'Business verification';
  const isOpensearchInProgress = screeningStatus?.inProgress === true;
  const isScreeningDelayed =
    isEntityDatabase &&
    screeningStatus?.health === 'delayed' &&
    (isOpensearchInProgress || isVerificationInProgress || isAiInProgress);
  const screeningProcessed = screeningStatus?.run?.entitiesProcessed;
  const screeningTotal = screeningStatus?.run?.entitiesTotal;
  const screeningCountLabel =
    typeof screeningTotal === 'number' && screeningTotal > 0
      ? `${Number(screeningProcessed) || 0} of ${screeningTotal}`
      : null;
  const latestScreeningErrorMetadata = screeningStatus?.errorMetadata ?? null;
  const latestScreeningErrorMessage = screeningStatus?.errorMessage ?? '';
  const latestScreeningWarningMetadata =
    screeningStatus?.warningMetadata ?? null;
  const latestScreeningWarningMessage = screeningStatus?.warningMessage ?? '';
  const showScreeningFailureBanner =
    !bannerDismissedForRetry &&
    database?.type === EntitiesType &&
    !screeningStatus?.inProgress &&
    (Boolean(latestScreeningWarningMetadata || latestScreeningWarningMessage) ||
      (screeningStatus?.status === 'failed' &&
        Boolean(latestScreeningErrorMetadata || latestScreeningErrorMessage)));
  const screeningFailureWarning = useMemo(() => {
    if (!showScreeningFailureBanner) {
      return null;
    }

    const activeMeta =
      latestScreeningWarningMetadata || latestScreeningErrorMetadata || null;
    const failedChunkIndex = activeMeta?.failed_chunk_index;
    const failedTotalChunks = activeMeta?.failed_total_chunks;
    const failedRecordIds: string[] = Array.isArray(
      activeMeta?.failed_record_ids,
    )
      ? Array.from(
          new Set(
            (activeMeta.failed_record_ids as unknown[]).filter(
              (id: unknown): id is string =>
                typeof id === 'string' && id.length > 0,
            ),
          ),
        )
      : [];
    const impactedCount = failedRecordIds.length;
    const chunkSuffix =
      Number.isFinite(failedChunkIndex) && Number.isFinite(failedTotalChunks)
        ? ` (Batch ${failedChunkIndex}/${failedTotalChunks})`
        : '';
    const baseMessage =
      latestScreeningWarningMetadata?.human_message ||
      latestScreeningWarningMessage ||
      latestScreeningErrorMetadata?.human_message ||
      latestScreeningErrorMessage ||
      'Latest screening failed after partial processing.';
    const paginationHint = latestScreeningWarningMetadata
      ? impactedCount > 0
        ? ` ${impactedCount} row(s) are impacted. Indicators appear on the pages containing those rows.`
        : ' Some results are impacted. Indicators appear on the pages containing affected rows when available.'
      : '';

    return {
      show: true,
      message: `${baseMessage}${chunkSuffix}${paginationHint}`,
      errorCode: latestScreeningErrorMetadata?.error_code,
      failedRecordIds: failedRecordIds,
    };
  }, [
    showScreeningFailureBanner,
    latestScreeningWarningMetadata,
    latestScreeningWarningMessage,
    latestScreeningErrorMetadata,
    latestScreeningErrorMessage,
  ]);

  const hasHistoryPagination = Boolean(paginatedAuditLogsData?.pagination);
  const totalHistoryPages = Math.max(
    1,
    paginatedAuditLogsData?.pagination?.totalPages ?? historyPage,
  );

  useEffect(() => {
    if (
      !isAuditLogsLoading &&
      hasHistoryPagination &&
      historyPage > totalHistoryPages
    ) {
      setHistoryPage(totalHistoryPages);
    }
  }, [
    hasHistoryPagination,
    historyPage,
    isAuditLogsLoading,
    totalHistoryPages,
  ]);

  // Any Audit Log filter/search change invalidates the current offset -- jump
  // back to page 1 rather than risk landing past the end of a now-shorter set.
  useEffect(() => {
    setHistoryPage(1);
  }, [auditFilters, debouncedAuditSearch]);

  useEffect(() => {
    if (generatedReportsPage > generatedReportsTotalPages) {
      setGeneratedReportsPage(generatedReportsTotalPages);
    }
  }, [generatedReportsPage, generatedReportsTotalPages]);

  useEffect(() => {
    // Action-lock signal. For entity databases, screening runs in two stages
    // (OpenSearch, then AI + verification); keep actions locked until BOTH
    // finish, even though the blur lifts after the OpenSearch stage. Other
    // database types keep their original single-phase lock.
    const inProgress = isEntityDatabase
      ? screeningStatus?.inProgress === true ||
        screeningStatus?.aiInProgress === true ||
        screeningStatus?.verificationInProgress === true
      : screeningStatus?.inProgress === true;
    setIsScreeningCurrently(Boolean(inProgress));
  }, [screeningStatus, isEntityDatabase]);

  const prevMainScreeningInProgressRef = useRef(false);
  useEffect(() => {
    const mainInProgress = screeningStatus?.inProgress === true;
    const aiInProgress = screeningStatus?.aiInProgress === true;
    const aiStatus = screeningStatus?.aiStatus;
    const now = Date.now();

    if (
      !isEntityDatabase &&
      prevMainScreeningInProgressRef.current &&
      !mainInProgress
    ) {
      aiPhaseDeadlineRef.current = now + 45_000;
      if (!aiInProgress && aiStatus !== 'complete') {
        setIsAwaitingAiPhase(true);
      }
    }

    if (aiInProgress || aiStatus === 'complete') {
      aiPhaseDeadlineRef.current = null;
      setIsAwaitingAiPhase(false);
    } else if (
      aiPhaseDeadlineRef.current &&
      now >= aiPhaseDeadlineRef.current
    ) {
      aiPhaseDeadlineRef.current = null;
      setIsAwaitingAiPhase(false);
    }

    prevMainScreeningInProgressRef.current = mainInProgress;
  }, [
    isEntityDatabase,
    screeningStatus?.inProgress,
    screeningStatus?.aiInProgress,
    screeningStatus?.aiStatus,
  ]);

  const prevScreeningStatusRef = useRef(screeningStatus);
  useEffect(() => {
    const prevStatus = prevScreeningStatusRef.current;

    // Refetch the list when the screening *phase* changes (started, a stage
    // finished, the run completed or failed) -- not on every progress tick,
    // and not when the first status arrives (the list just loaded itself).
    const phaseChanged =
      Boolean(prevStatus) &&
      Boolean(screeningStatus) &&
      (prevStatus!.inProgress !== screeningStatus!.inProgress ||
        prevStatus!.aiInProgress !== screeningStatus!.aiInProgress ||
        prevStatus!.verificationInProgress !==
          screeningStatus!.verificationInProgress ||
        prevStatus!.verificationPhase !== screeningStatus!.verificationPhase ||
        prevStatus!.status !== screeningStatus!.status);

    if (phaseChanged) {
      refetchItems();

      const screeningPhaseEnded =
        Boolean(prevStatus) &&
        Boolean(screeningStatus) &&
        ((prevStatus!.inProgress && !screeningStatus!.inProgress) ||
          (prevStatus!.aiInProgress && !screeningStatus!.aiInProgress) ||
          (prevStatus!.verificationInProgress &&
            !screeningStatus!.verificationInProgress) ||
          (prevStatus!.status !== screeningStatus!.status &&
            ['complete', 'failed', 'completed'].includes(
              String(screeningStatus!.status || '').toLowerCase(),
            )));

      if (database?.type === EntitiesType && screeningPhaseEnded) {
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: databaseKeys.databases(),
          exact: false,
        });
      }
    }

    prevScreeningStatusRef.current = screeningStatus;
  }, [screeningStatus, refetchItems, database?.type, queryClient]);

  const screeningProgress = useMemo(() => {
    if (screeningStatus && screeningStatus.run && isScreeningCurrently) {
      const cp = screeningStatus.run.entitiesProcessed ?? 0;
      const ct = screeningStatus.run.entitiesTotal ?? 0;
      const percent =
        typeof screeningStatus.run.percent === 'number'
          ? screeningStatus.run.percent
          : ct > 0
          ? Math.round((cp / ct) * 100)
          : 0;
      return {
        chunksProcessed: cp,
        chunksTotal: ct,
        progressPercentage: percent,
      };
    }
    const latestScreening = screeningHistoryData[0];
    if (!isScreeningCurrently || !latestScreening?.chunk_total) {
      return null;
    }
    return {
      chunksProcessed: latestScreening.chunk_processed || 0,
      chunksTotal: latestScreening.chunk_total,
      progressPercentage: parseFloat(
        (
          (latestScreening.chunk_processed / latestScreening.chunk_total) *
          100
        ).toFixed(2),
      ),
    };
  }, [screeningStatus, screeningHistoryData, isScreeningCurrently]);

  const currentHistoryItems = combinedHistoryData;

  // Mutations
  const updateDatabaseEntitiesStatusMutation = useUpdateDatabaseEntitiesStatus(
    cur_user?.selected_team?.id,
    database?.id,
  );
  const removeDatabaseEntitiesMutation = useRemoveDatabaseEntities(
    cur_user?.selected_team?.id,
    database?.id,
  );
  const deleteDatabaseMutation = useDeleteDatabase();
  const retryScreeningMutation = useRunRetryScreening();
  const createBulkWithoutDuplicateCheckMutation =
    useCreateBulkEntitiesWithoutDuplicateCheck();
  const restorePreviousStatusMutation = useRestorePreviousStatus(
    database?.id,
    cur_user?.selected_team?.id,
  );
  const declinePreviousStatusMutation = useDeclinePreviousStatus(
    database?.id,
    cur_user?.selected_team?.id,
  );
  const restorePreviousStatusByFlagMutation = useRestorePreviousStatusByFlag(
    database?.id,
    cur_user?.selected_team?.id,
  );
  const [loadingRestoreByFlag, setLoadingRestoreByFlag] = useState<
    (typeof STATUS_RESET_PREVIOUS_FLAGS)[number] | null
  >(null);
  const [pendingRestoreByFlag, setPendingRestoreByFlag] = useState<
    (typeof STATUS_RESET_PREVIOUS_FLAGS)[number] | null
  >(null);

  const updateTariffStatusMutation = useUpdateTariffStatus(database?.id);
  // const deleteTariffMutation = useDeleteTariff(database?.id);
  const deleteBulkTariffsMutation = useDeleteBulkTariffs(database?.id);
  const runCheckMutation = useRunTariffCheck();
  // const createBulkTariffsWOtDupCheckMutation = useCreateBulkTariffsWithoutDuplicateCheck();

  const updateProductStatusMutation = useUpdateProductStatus(database?.id);
  const deleteBulkProductsMutation = useDeleteBulkProducts(database?.id);
  const runEntityServiceScreeningMutation =
    useRunEntityServiceDatabaseScreening();
  const isEntityServiceScreeningPending =
    runEntityServiceScreeningMutation.isPending;

  // After a run starts, mark its history stale once the server has written
  // it. Invalidation refetches only enabled queries, so this sends nothing
  // unless the history tab is open. The list itself refetches when the
  // status reports the new phase.
  const refreshHistoryAfterRunStart = useCallback(() => {
    const databaseId = database?.id ? String(database.id) : '';
    if (!databaseId) {
      return;
    }
    window.setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: [...screeningKeys.byDatabase(databaseId), 'runs'],
      });
      queryClient.invalidateQueries({ queryKey: auditKeys.auditLogs() });
    }, 2000);
  }, [database?.id, queryClient]);

  // Entity creates invalidate the entity lists themselves, and upload
  // completion refetches through the socket handler above, so the modals
  // only refresh product and tariff lists by hand.
  const refreshListAfterModal =
    database.type === EntitiesType ? undefined : refetchItems;

  const isStatusUpdating =
    updateDatabaseEntitiesStatusMutation.isPending ||
    updateProductStatusMutation.isPending ||
    updateTariffStatusMutation.isPending;

  const statusResetChipCount = statusResetFromQuery
    ? paginationData?.totalItems ?? statusResetCount
    : statusResetCount;

  const selectedWithPreviousStatus = useMemo(
    () =>
      selectedItems.filter(
        (item) => item?.previous_status_flag && item?.association_id,
      ),
    [selectedItems],
  );

  const handleReviewStatusResets = useCallback(() => {
    if (isScreeningCurrently) {
      return;
    }
    handleUrlChange({ statusReset: 'true', page: 1 });
  }, [handleUrlChange, isScreeningCurrently]);

  const handleDismissStatusResetBanner = useCallback(() => {
    if (isScreeningCurrently) {
      return;
    }
    setStatusResetBannerDismissedCount(statusResetCount);
  }, [isScreeningCurrently, statusResetCount]);

  const handleClearStatusResetFilter = useCallback(() => {
    if (isScreeningCurrently) {
      return;
    }
    handleUrlChange({ statusReset: '', page: 1 });
  }, [handleUrlChange, isScreeningCurrently]);

  const handleBulkRestorePrevious = useCallback(() => {
    const ids = selectedWithPreviousStatus.map((item) =>
      String(item.association_id),
    );
    if (!ids.length) {
      return;
    }
    restorePreviousStatusMutation.mutate(ids, {
      onSuccess: (result) => {
        enqueueSnackbar(
          result?.message ||
            `Restored previous status for ${
              result?.data?.updatedCount ?? ids.length
            } entit${
              (result?.data?.updatedCount ?? ids.length) === 1 ? 'y' : 'ies'
            }`,
          { variant: 'success' },
        );
        setSelectedItems([]);
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || 'Failed to restore previous status',
          { variant: 'error' },
        );
      },
    });
  }, [restorePreviousStatusMutation, selectedWithPreviousStatus]);

  const handleBulkDeclinePrevious = useCallback(() => {
    const ids = selectedWithPreviousStatus.map((item) =>
      String(item.association_id),
    );
    if (!ids.length) {
      return;
    }
    declinePreviousStatusMutation.mutate(ids, {
      onSuccess: (result) => {
        enqueueSnackbar(
          result?.message ||
            `Declined previous status for ${
              result?.data?.updatedCount ?? ids.length
            } entit${
              (result?.data?.updatedCount ?? ids.length) === 1 ? 'y' : 'ies'
            }`,
          { variant: 'success' },
        );
        setSelectedItems([]);
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || 'Failed to decline previous status',
          { variant: 'error' },
        );
      },
    });
  }, [declinePreviousStatusMutation, selectedWithPreviousStatus]);

  const getPreviousStatusRestoreCount = useCallback(
    (flag: (typeof STATUS_RESET_PREVIOUS_FLAGS)[number]) => {
      if (flag === 'quarantined') {
        return Number(stats?.total_status_reset_quarantined_entities || 0);
      }
      if (flag === 'barred') {
        return Number(stats?.total_status_reset_barred_entities || 0);
      }
      return Number(stats?.total_status_reset_cleared_entities || 0);
    },
    [stats],
  );

  const requestRestorePreviousByFlag = useCallback(
    (previousStatusFlag: (typeof STATUS_RESET_PREVIOUS_FLAGS)[number]) => {
      if (!database?.id || loadingRestoreByFlag || isScreeningCurrently) {
        return;
      }
      setPendingRestoreByFlag(previousStatusFlag);
    },
    [database?.id, isScreeningCurrently, loadingRestoreByFlag],
  );

  const closeRestoreByFlagConfirmation = useCallback(() => {
    if (loadingRestoreByFlag) {
      return;
    }
    setPendingRestoreByFlag(null);
  }, [loadingRestoreByFlag]);

  const pendingRestoreByFlagCount = pendingRestoreByFlag
    ? getPreviousStatusRestoreCount(pendingRestoreByFlag)
    : 0;
  const pendingRestoreByFlagLabel = pendingRestoreByFlag
    ? pendingRestoreByFlag.charAt(0).toUpperCase() +
      pendingRestoreByFlag.slice(1)
    : '';
  const pendingRestoreByFlagDescription = pendingRestoreByFlag
    ? `This will restore ${pendingRestoreByFlagCount} ${
        pendingRestoreByFlagCount === 1 ? 'entity' : 'entities'
      } that ${
        pendingRestoreByFlagCount === 1 ? 'was' : 'were'
      } previously ${pendingRestoreByFlagLabel}.`
    : undefined;

  const handleRestorePreviousByFlag = useCallback(async () => {
    const previousStatusFlag = pendingRestoreByFlag;
    if (
      !previousStatusFlag ||
      !database?.id ||
      loadingRestoreByFlag ||
      isScreeningCurrently
    ) {
      return;
    }
    setLoadingRestoreByFlag(previousStatusFlag);
    try {
      const result = await restorePreviousStatusByFlagMutation.mutateAsync({
        databaseId: database.id,
        previous_status_flag: previousStatusFlag,
      });
      enqueueSnackbar(
        result?.message || `Restored all that were ${previousStatusFlag}`,
        { variant: 'success' },
      );
      setSelectedItems([]);
      setPendingRestoreByFlag(null);
    } catch (error: any) {
      enqueueSnackbar(
        error?.message ||
          `Failed to restore entities that were ${previousStatusFlag}`,
        { variant: 'error' },
      );
    } finally {
      setLoadingRestoreByFlag(null);
    }
  }, [
    database?.id,
    isScreeningCurrently,
    loadingRestoreByFlag,
    pendingRestoreByFlag,
    restorePreviousStatusByFlagMutation,
  ]);

  // Handlers
  const handleTabSwitch = (newTab: string) => {
    handleUrlChange({ tab: newTab, page: 1 });
    if (newTab === 'Screening History') {
      setHistoryPage(1);
    }
    if (newTab === 'Generated Reports') {
      setGeneratedReportsPage(1);
    }
    setSelectedItems([]);
  };

  const fetchResultsReports = useCallback(
    async (options?: { silent?: boolean; page?: number }) => {
      if (!database?.id || !cur_user?.selected_team?.id) {
        setResultsReports([]);
        setGeneratedReportsTotalPages(1);
        return;
      }

      const page = options?.page || generatedReportsPage;
      const isSilent = options?.silent === true;
      if (isSilent) {
        setIsRefreshingResultsReports(true);
      } else {
        setIsLoadingResultsReports(true);
      }

      try {
        const response = await listResultsReportsForDatabase(
          database.id,
          cur_user.selected_team.id,
          { page: page, limit: GENERATED_REPORTS_ITEMS_PER_PAGE },
        );
        setResultsReports(response.data);
        setGeneratedReportsTotalPages(
          Math.max(1, response.pagination.totalPages || 1),
        );
      } catch (error: any) {
        setResultsReports([]);
        setGeneratedReportsTotalPages(1);
        enqueueSnackbar(
          error?.message || 'Failed to fetch generated report history',
          { variant: 'error' },
        );
      } finally {
        if (isSilent) {
          setIsRefreshingResultsReports(false);
        } else {
          setIsLoadingResultsReports(false);
        }
      }
    },
    [database?.id, cur_user?.selected_team?.id, generatedReportsPage],
  );

  useEffect(() => {
    if (tab !== 'Generated Reports') {
      return undefined;
    }
    fetchResultsReports({ page: generatedReportsPage });
    const interval = setInterval(() => {
      if (isDocumentHidden()) {
        return;
      }
      fetchResultsReports({ silent: true, page: generatedReportsPage });
    }, 10000);
    return () => clearInterval(interval);
  }, [tab, fetchResultsReports, generatedReportsPage]);

  const handleDownloadExistingReport = useCallback(
    async (row: ResultsReportRow) => {
      if (!row?.jobId) {
        enqueueSnackbar('This report row has no job identifier.', {
          variant: 'error',
        });
        return;
      }

      const groupId = cur_user?.selected_team?.id;
      if (!groupId) {
        enqueueSnackbar('No team selected. Cannot download report.', {
          variant: 'error',
        });
        return;
      }

      setIsDownloadingReportJobId(row.jobId);
      try {
        const payload = await getResultsReportDownloadUrl(row.jobId, groupId);
        if (!payload?.url) {
          throw new Error('Download URL was not returned.');
        }
        window.open(payload.url, '_blank', 'noopener,noreferrer');
      } catch (error: any) {
        enqueueSnackbar(error?.message || 'Failed to prepare report download', {
          variant: 'error',
        });
      } finally {
        setIsDownloadingReportJobId(null);
      }
    },
    [cur_user?.selected_team?.id],
  );

  const exportToCSV = (filename: string, data: any, headers: any) => {
    const csvRows = [];
    csvRows.push(headers.join(','));
    data.forEach((item: any) => {
      const values = headers.map((header: any) => {
        const value = item[header] || '';
        const escaped = `${value}`.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });
    const csvString = csvRows.join('\r\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      enqueueSnackbar('Browser does not support CSV export.', {
        variant: 'error',
      });
    }
  };

  const getExportHeaders = () => {
    if (database.type === 'products') {
      return [
        'name',
        'description',
        'category',
        'hierarchyType',
        'manufacturer',
        'model_number',
        'specifications',
        'country_of_origin',
        'status_flag',
        'created_at',
        'updated_at',
      ];
    }
    return [
      'HTS Code (10 dgit)',
      'Country of Origin (ISO-2)',
      'Product Value',
      'Transportation Method',
      'Entry Date (YYYY-MM-DD)',
      'Loading Date (YYYY-MM-DD)',
      'Country of Smelt / Pour (ISO-2)',
      'Material Percentage (.xx)',
      'Material Weight / Amount',
      // Reported quantity + its unit — what prices the specific (per-unit)
      // portion of a compound duty rate. Exported so a download/edit/re-upload
      // round-trip keeps them.
      'Quantity',
      'Unit of Measure',
    ];
  };

  const getExportData = async () => {
    if (database.type === 'tariffs') {
      return (await getAllTariffsByDatabaseId(database.id));
    }
    const exportPageParams: PaginationParams = {
      page: 1,
      limit: 100000,
      sortBy: 'name asc',
      sortOrder: sortOrder,
      tab: 'Export',
    };
    return (
      await getProductsByDatabaseId(database.id, {
        ...exportPageParams,
      })
    ).data;
  };

  const handleExportData = async () => {
    if (!database?.id) {
      enqueueSnackbar('Database ID is missing', { variant: 'error' });
      return;
    }

    setIsExporting(true);
    try {
      const exportData = await getExportData();

      if (!exportData || exportData.length === 0) {
        enqueueSnackbar('No data available to export', { variant: 'warning' });
        return;
      }

      const headers = getExportHeaders();
      const itemType = database.type === 'products' ? 'products' : 'tariffs';
      let finalFileName =
        exportFileName.trim() ||
        `${database.name}_${itemType}_${
          new Date().toISOString().split('T')[0]
        }`;
      if (!finalFileName.toLowerCase().endsWith('.csv')) {
        finalFileName += '.csv';
      }

      exportToCSV(finalFileName, exportData, headers);
      enqueueSnackbar(
        `Successfully exported ${exportData.length} ${itemType} to CSV`,
        { variant: 'success' },
      );
      setIsExportModalOpen(false);
    } catch (error: any) {
      enqueueSnackbar(
        `Failed to export data: ${error?.message || 'Unknown error'}`,
        { variant: 'error' },
      );
    } finally {
      setIsExporting(false);
    }
  };






  const handleExportTariffs = async () => {
    try {
      // Same CBP 7501 layout as before, now built and streamed by the analyzer
      // service (format=BITE_DEFAULT) instead of assembled in this tab.
      const { total, truncated } = await downloadTariffCsv(
        database.id,
        'BITE_DEFAULT',
        exportFileName.trim() ||
          `${database.name}_CBP_Export_${format(new Date(), 'yyyy-MM-dd')}`,
      );

      if (truncated) {
        enqueueSnackbar(
          `Export capped: this database has ${total.toLocaleString()} rows and the file does not contain all of them.`,
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar('Successfully exported tariff data', { variant: 'success' });
      }
      setIsExportModalOpen(false);
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(
        `Failed to export data: ${error?.message || 'Unknown error'}`,
        { variant: 'error' },
      );
    }
  };

  const handleSort = (key: string) => {
    const newSortOrder = sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
    handleUrlChange({ sortBy: key, sortOrder: newSortOrder });
  };

  const handleSelectAll = () => {
    setSelectedItems(
      selectedItems.length === displayedRowsForSelection.length
        ? []
        : displayedRowsForSelection,
    );
  };

  const handleDeleteDatabase = async () => {
    if (!database?.id) {
      return enqueueSnackbar('Database ID is missing', { variant: 'error' });
    }
    setIsDeleteModalOpen(true);
    await deleteDatabaseMutation.mutateAsync(
      { id: database.id, groupId: cur_user?.selected_team?.id },
      {
        onSuccess: () => {
          enqueueSnackbar('Your database has been deleted successfully', {
            variant: 'success',
          });
          history.push('/bite-db/entities');
        },
        onError: (error: any) =>
          enqueueSnackbar(error.message || 'Failed to delete database', {
            variant: 'error',
          }),
        onSettled: () => setIsDeleteModalOpen(false),
      },
    );
  };

  const handleEdit = async (item: any) => {
    setSelectedItems((prevSelected: any[]) => {
      const safePrevSelected = Array.isArray(prevSelected) ? prevSelected : [];
      const alreadySelected = safePrevSelected.some((i) => i.id === item.id);
      if (alreadySelected) {
        return safePrevSelected;
      }
      return [...safePrevSelected, item];
    });
    setIsChangingStatus(true);
  };

  const handleProductEdit = async (item: any) => {
    setEditProductItem(item);
    setEditProductCatelogModalOpen(true);
  };

  const runScreeningForDatabase = async () => {
    if (!database.id) {
      return enqueueSnackbar('Database ID is missing', { variant: 'error' });
    }
    const response = await runEntityServiceScreeningMutation.mutateAsync({
      databaseId: String(database.id),
      screenedBy: cur_user?.basics?.email || cur_user?.basics?.id,
    });

    if (response.success) {
      enqueueSnackbar('Successfully started a new screening', {
        variant: 'success',
      });
      requestScreeningCheck();
      refreshHistoryAfterRunStart();
      return;
    }

    enqueueSnackbar(
      `Failed to run screening: ${response?.result || 'Unknown error'}`,
      {
        variant: 'error',
      },
    );
  };

  const runCheckForDatabase = async () => {
    if (!database.id) {
      return enqueueSnackbar('Database ID is missing', { variant: 'error' });
    }
    runCheckMutation.mutate(
      { databaseIds: [database.id] },
      {
        onSuccess: () => {
          enqueueSnackbar('Successfully started a new tariff check', {
            variant: 'success',
          });
          refreshHistoryAfterRunStart();
        },
        onError: (error: any) =>
          enqueueSnackbar(
            `Failed to run check: ${error?.message || 'Unknown error'}`,
            {
              variant: 'error',
            },
          ),
      },
    );
  };

  const handleBulkAction = async (
    action: 'delete' | 'updateStatus',
    status?: string,
  ) => {
    const deleteIds = selectedItems.map((item) => item.id);
    const updateIds = selectedItems.map(
      (item) => item.database_tariff_id || item.association_id,
    );
    const databaseEntityIds = selectedItems
      .map((item) => item.association_id)
      .filter(Boolean)
      .map(String);

    try {
      if (action === 'delete' && database.type === EntitiesType) {
        if (databaseEntityIds.length === 0) {
          return enqueueSnackbar('Please select at least one entity', {
            variant: 'error',
          });
        }

        const result = await removeDatabaseEntitiesMutation.mutateAsync(
          databaseEntityIds,
        );
        enqueueSnackbar(
          `Removed ${result?.removed ?? 0} ${
            result?.removed === 1 ? 'entity' : 'entities'
          } from the Database`,
          { variant: 'success' },
        );
      } else if (action === 'delete') {
        if (deleteIds.length === 0) {
          return enqueueSnackbar('Please select at least one item', {
            variant: 'error',
          });
        }

        const deleteMutation =
          database.type === 'tariffs'
            ? deleteBulkTariffsMutation
            : deleteBulkProductsMutation;

        const result = await deleteMutation.mutateAsync(deleteIds);
        if (result.successful_deletes > 0) {
          enqueueSnackbar(
            `Successfully deleted ${result.successful_deletes} items`,
            {
              variant: 'success',
            },
          );
        }
        if (result.failed_deletes > 0) {
          enqueueSnackbar(`Failed to delete ${result.failed_deletes} items`, {
            variant: 'warning',
          });
        }
      } else {
        // Handle status updates
        if (database.type === 'tariffs') {
          const promises = updateIds.map((id) =>
            updateTariffStatusMutation.mutateAsync({
              id: id,
              statusData: { status_flag: status || '' },
            }),
          );
          await Promise.all(promises);
        } else if (database.type === 'products') {
          const promises = updateIds.map((id) =>
            updateProductStatusMutation.mutateAsync({
              id: id,
              statusData: { status_flag: status || '' },
            }),
          );
          await Promise.all(promises);
        } else if (isStatusFlag(status)) {
          await updateDatabaseEntitiesStatusMutation.mutateAsync({
            databaseEntityIds: databaseEntityIds,
            toStatus: status,
          });
        } else {
          throw new Error('Please select a status');
        }

        enqueueSnackbar(
          database.type === EntitiesType
            ? 'Entities updated successfully'
            : 'Items updated successfully',
          {
            variant: 'success',
          },
        );
      }
    } catch (error: any) {
      const failure =
        database.type === EntitiesType
          ? `Entities could not be ${
              action === 'delete' ? 'removed from the Database' : 'updated'
            }`
          : `Some items failed to be ${
              action === 'delete' ? 'deleted' : 'updated'
            }`;
      enqueueSnackbar(error.message || failure, {
        variant: 'error',
      });
    } finally {
      setSelectedItems([]);
      setIsDeletingItems(false);
      setIsChangingStatus(false);
      setShowStatusConfirmModal(false);
      setPendingStatus(null);
      // Entity status/remove mutations invalidate this list themselves, once.
      // Product and tariff row mutations don't all reach their list key, so
      // they still refetch.
      if (database.type !== EntitiesType) {
        refetchItems();
      }
    }
  };

  const handleStatusSelection = (status: string) => {
    if (!status) {
      enqueueSnackbar('Please select a status', { variant: 'warning' });
      return;
    }
    if (selectedItems.length === 0) {
      enqueueSnackbar(
        `Please select at least one ${
          database.type === EntitiesType ? 'entity' : 'item'
        }`,
        { variant: 'error' },
      );
      return;
    }
    setPendingStatus(status);
    setShowStatusConfirmModal(true);
    setIsChangingStatus(false);
  };

  const closeStatusConfirmation = () => {
    setShowStatusConfirmModal(false);
    setPendingStatus(null);
  };

  const confirmStatusChange = () => {
    if (!pendingStatus) {
      return;
    }
    handleBulkAction('updateStatus', pendingStatus);
  };

  const handleApplyDatabaseFilters = ({
    statuses,
    risks,
    hitCountRanges,
    nameStartsWith,
    hasNewResults,
    lenses,
  }: {
    statuses: string[];
    risks: string[];
    hitCountRanges: string[];
    nameStartsWith: string[];
    hasNewResults: 'yes' | 'no' | null;
    lenses: string[];
  }) => {
    setStatusFilters(statuses);
    setRiskFilters(risks);
    setHitCountRanges(hitCountRanges);
    setNameStartsWith(nameStartsWith);
    setHasNewResultsFilter(hasNewResults);
    setLensFilters(lenses);
    handleUrlChange({
      page: 1,
      statusFilters: statuses.length ? statuses.join(',') : '',
      riskFilters: risks.length ? risks.join(',') : '',
      lensFilters: lenses.length ? lenses.join(',') : '',
      hitCountRanges: hitCountRanges.length ? hitCountRanges.join(',') : '',
      nameStartsWith: nameStartsWith.length ? nameStartsWith.join(',') : '',
      hasNewResults: hasNewResults ? hasNewResults : '',
    });
  };
  const handleSingleDuplicatesDetected = useCallback(
    ({
      duplicates,
      fallbackEntities,
    }: {
      duplicates: DuplicateEntity[];
      fallbackEntities: any[];
    }) => {
      setShowAddItemModal(false);
      setDuplicateReviewState({
        open: true,
        duplicates: duplicates,
        fallbackEntities: fallbackEntities || [],
      });
    },
    [],
  );

  const resetDuplicateReviewState = useCallback(() => {
    setDuplicateReviewState({
      open: false,
      duplicates: [],
      fallbackEntities: [],
    });
  }, []);

  const handleDuplicateDiscard = useCallback(() => {
    if (duplicateReviewState.duplicates.length > 0) {
      enqueueSnackbar(
        `${duplicateReviewState.duplicates.length} duplicate${
          duplicateReviewState.duplicates.length > 1 ? 's' : ''
        } discarded.`,
        { variant: 'info' },
      );
    }
    resetDuplicateReviewState();
  }, [duplicateReviewState.duplicates.length, resetDuplicateReviewState]);

  const handleDuplicateAddToReview = useCallback(
    (selectedDuplicates?: DuplicateEntity[]) => {
      if (!database?.id) {
        enqueueSnackbar('Database ID is missing', { variant: 'error' });
        return;
      }

      let entitiesToAdd: any[] = [];

      if (selectedDuplicates) {
        entitiesToAdd = selectedDuplicates
          .map((duplicate) => {
            if (!duplicate?.entity) {
              return null;
            }
            return {
              ...duplicate.entity,
              database_id: duplicate.entity.database_id || database.id,
              created_by:
                duplicate.entity.created_by || cur_user.basics?.id || '',
            };
          })
          .filter((entity) => Boolean(entity));
      } else if (duplicateReviewState.fallbackEntities.length > 0) {
        entitiesToAdd = duplicateReviewState.fallbackEntities;
      } else {
        entitiesToAdd = duplicateReviewState.duplicates
          .map((duplicate) => {
            if (!duplicate?.entity) {
              return null;
            }
            return {
              ...duplicate.entity,
              database_id: duplicate.entity.database_id || database.id,
              created_by:
                duplicate.entity.created_by || cur_user.basics?.id || '',
            };
          })
          .filter((entity) => Boolean(entity));
      }

      if (!entitiesToAdd.length) {
        enqueueSnackbar('No duplicates available to add.', {
          variant: 'warning',
        });
        return;
      }

      createBulkWithoutDuplicateCheckMutation.mutate(
        {
          entities: entitiesToAdd,
          databaseId: database.id,
          createdBy: cur_user.basics?.id || '',
          teamId: cur_user.selected_team?.id || '',
        },
        {
          onSuccess: () => {
            enqueueSnackbar(
              `${entitiesToAdd.length} entit${
                entitiesToAdd.length > 1 ? 'ies' : 'y'
              } added to review queue successfully.`,
              { variant: 'success' },
            );
            resetDuplicateReviewState();
          },
          onError: (error: any) => {
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              'Failed to add duplicates to review queue';
            enqueueSnackbar(errorMessage, { variant: 'error' });
          },
        },
      );
    },
    [
      createBulkWithoutDuplicateCheckMutation,
      cur_user.basics?.id,
      cur_user.selected_team?.id,
      database?.id,
      duplicateReviewState.fallbackEntities,
      duplicateReviewState.duplicates,
      enqueueSnackbar,
      refetchItems,
      resetDuplicateReviewState,
    ],
  );

  // Effects for Error Handling
  useEffect(() => {
    if (itemsError) {
      enqueueSnackbar('Failed to fetch entities', { variant: 'error' });
    }
  }, [itemsError]);
  useEffect(() => {
    if (screeningHistoryError) {
      enqueueSnackbar('Failed to fetch screening history', {
        variant: 'error',
      });
    }
  }, [screeningHistoryError]);

  useEffect(() => {
    if (auditLogsError) {
      enqueueSnackbar('Failed to fetch audit logs', {
        variant: 'error',
      });
    }
  }, [auditLogsError]);

  return (
    <div className='team-content-container'>
      <div className='people-col'>
        <div className='team-content-header' style={{ gap: '10px' }}>
          <div className='team-content-search'>
            <div className='card-db-header'>
              <div className='card-db-icon'>
                {database.type === 'tariffs' ? (
                  <PriceCheckOutlinedIcon
                    sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
                  />
                ) : (
                  <PeopleAltOutlinedIcon
                    sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
                  />
                )}
              </div>
              <div className='card-db-title'>{database.name}</div>
              {isBulkUploading && (
                <span
                  aria-live='polite'
                  className='bite-status-pill bite-status-pill--upload'
                >
                  <span aria-hidden='true' className='bite-status-pill__dot' />
                  Processing upload
                </span>
              )}
              {isEntityDatabase &&
                isOpensearchInProgress &&
                !isBulkUploading && (
                  <span
                    aria-live='polite'
                    className={`bite-status-pill ${
                      isScreeningDelayed
                        ? 'bite-status-pill--delayed'
                        : 'bite-status-pill--screening'
                    }`}
                  >
                    <span aria-hidden='true' className='bite-status-pill__dot' />
                    {isScreeningDelayed
                      ? DELAYED_SCREENING_MESSAGE
                      : 'Screening in progress'}
                    {screeningCountLabel ? (
                      <span className='bite-status-pill__count'>
                        {screeningCountLabel}
                      </span>
                    ) : null}
                  </span>
                )}
              {isEntityDatabase &&
                isScreeningDelayed &&
                !isOpensearchInProgress &&
                !isBulkUploading &&
                (isAiInProgress || isVerificationInProgress) && (
                  <span
                    aria-live='polite'
                    className='bite-status-pill bite-status-pill--delayed'
                  >
                    <span aria-hidden='true' className='bite-status-pill__dot' />
                    {DELAYED_SCREENING_MESSAGE}
                  </span>
                )}
              {isAiInProgress &&
                !isVerificationInProgress &&
                !isBulkUploading && (
                  <span
                    aria-live='polite'
                    className='bite-status-pill bite-status-pill--ai'
                  >
                    <span aria-hidden='true' className='bite-status-pill__dot' />
                    AI risk evaluation
                  </span>
                )}
              {isVerificationInProgress && !isBulkUploading && (
                <span
                  aria-live='polite'
                  className='bite-status-pill bite-status-pill--verify'
                >
                  <span aria-hidden='true' className='bite-status-pill__dot' />
                  {verificationPhaseLabel}
                </span>
              )}
            </div>
          </div>
          <Tooltip
            title={
              isScreeningCurrently
                ? 'Unavailable while screening is in progress'
                : 'Add Item'
            }
            placement='bottom-start'
            arrow
          >
            <div
              className='audit-btn-star'
              onClick={handleAddItemClick}
              aria-disabled={isScreeningCurrently}
              style={
                isScreeningCurrently
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : undefined
              }
            >
              <AddOutlinedIcon
                sx={{ fontSize: 20, color: 'var(--card-text)' }}
              />
            </div>
          </Tooltip>
          <Tooltip
            title={
              isScreeningCurrently || isBulkUploading
                ? 'Unavailable while processing is in progress'
                : 'Import Data'
            }
            placement='bottom-start'
            arrow
          >
            <div
              className='audit-btn-star'
              onClick={handleImportDataClick}
              aria-disabled={isScreeningCurrently || isBulkUploading}
              style={
                isScreeningCurrently || isBulkUploading
                  ? {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      padding: isBulkUploading ? 0 : undefined,
                    }
                  : undefined
              }
            >
              {isBulkUploading ? (
                <CircularProgress
                  size={20}
                  sx={{ color: 'var(--card-text)' }}
                />
              ) : (
                <FileUploadOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--card-text)' }}
                />
              )}
            </div>
          </Tooltip>
          {database.type === EntitiesType && (
            <Tooltip
              title={
                isScreeningCurrently || isBulkUploading
                  ? 'Unavailable while processing is in progress'
                  : 'Create Links'
              }
              placement='bottom-start'
              arrow
            >
              <div
                className='audit-btn-star'
                onClick={() => {
                  if (isScreeningCurrently || isBulkUploading) {
                    return;
                  }
                  setShowBulkLinkEntitiesModal(true);
                }}
                aria-disabled={isScreeningCurrently || isBulkUploading}
                style={
                  isScreeningCurrently || isBulkUploading
                    ? { opacity: 0.5, cursor: 'not-allowed' }
                    : undefined
                }
              >
                <AccountTreeOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--card-text)' }}
                />
              </div>
            </Tooltip>
          )}
          {database.type !== 'tariffs' && (
            <Tooltip
              title='Download Results Report'
              placement='bottom-start'
              arrow
            >
              <div
                className='audit-btn-star'
                onClick={() => setIsResultsReportModalOpen(true)}
              >
                <PictureAsPdfOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--card-text)' }}
                />
              </div>
            </Tooltip>
          )}
          {/* The change-report endpoints live only on the entity service, so
              entity-service-only. */}
          {isEntityDatabase && (
            <Tooltip
              title='Export Entities with Changes'
              placement='bottom-start'
              arrow
            >
              <div
                className='audit-btn-star'
                onClick={() => setIsChangesReportModalOpen(true)}
              >
                <DifferenceOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--card-text)' }}
                />
              </div>
            </Tooltip>
          )}
          {(database.type === 'products' || database.type === 'tariffs') && (
            <Tooltip title='Export Data' placement='bottom-start' arrow>
              <div
                className='audit-btn-star'
                onClick={() => {
                  if (!isExporting) {
                    const itemType =
                      database.type === 'tariffs' ? 'tariffs' : 'products';
                    setExportFileName(
                      `${database.name}_${itemType}_Export_${format(
                        new Date(),
                        'yyyy-MM-dd',
                      )}`,
                    );
                    setExportFormat('BITE_DEFAULT');
                    setIsExportModalOpen(true);
                  }
                }}
                style={
                  isExporting
                    ? { opacity: 0.5, cursor: 'not-allowed', padding: 0 }
                    : undefined
                }
              >
                {isExporting ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: 'var(--card-text)' }}
                  />
                ) : (
                  <FileDownloadOutlinedIcon
                    sx={{ fontSize: 20, color: 'var(--card-text)' }}
                  />
                )}
              </div>
            </Tooltip>
          )}
          <Tooltip title='Delete Database' placement='bottom-start' arrow>
            <div
              className='audit-btn-star'
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <DeleteForeverIcon
                sx={{ fontSize: 20, color: 'var(--card-text)' }}
              />
            </div>
          </Tooltip>
        </div>
        <div className='team-content-header'>
          <div className='team-content-search'>
            <span className='card-db-meta-span'>
              {database.type === EntitiesType
                ? formatPeopleCompaniesDatabaseMeta({
                    databaseName: database.name,
                    tab: tab,
                    listTotal: paginationData?.totalItems || 0,
                    databaseTotalEntities: stats?.total_entities,
                    hasSearchOrFilters:
                      Boolean(searchTerm) || hasActiveFilters,
                  })
                : `${database.name} Database • ${
                    paginationData?.totalItems || 0
                  } items`}
            </span>
          </div>
        </div>
      </div>

      {showScreeningFailureBanner && (
        <ScreeningFailureBanner
          metadata={
            latestScreeningWarningMetadata || latestScreeningErrorMetadata
          }
          message={screeningFailureWarning?.message}
          onRetry={() => {
            setBannerDismissedForRetry(true);
            if (!database?.id) return;
            queryClient.invalidateQueries({
              queryKey: [...screeningKeys.byDatabase(database.id), 'status'],
            });
            const failedIds = screeningFailureWarning?.failedRecordIds ?? [];
            if (failedIds.length > 0) {
              retryScreeningMutation.mutate(
                { databaseId: database.id, entityIds: failedIds },
                {
                  onSuccess: () => {
                    enqueueSnackbar('Retrying screening for failed entities', {
                      variant: 'success',
                    });
                    requestScreeningCheck();
                    refreshHistoryAfterRunStart();
                  },
                  onError: (error: any) =>
                    enqueueSnackbar(
                      `Failed to retry screening: ${
                        error?.message || 'Unknown error'
                      }`,
                      { variant: 'error' },
                    ),
                },
              );
            } else {
              runScreeningForDatabase();
            }
          }}
          onDismiss={() => setBannerDismissedForRetry(true)}
          onShowAffected={() => {
            const isCurrentlyFiltering = parsedImpactedByError === 'yes';
            // The URL change re-keys the list, which fetches it once.
            handleUrlChange({
              page: 1,
              impactedByError: isCurrentlyFiltering ? '' : 'yes',
            });
          }}
          isShowingAffected={parsedImpactedByError === 'yes'}
        />
      )}

      <div className='screening-tabs-container'>
        <div className='screening-tabs-inner'>
          {['All', 'Review Queue', 'Quarantined', 'Barred', 'Cleared'].map(
            (tabName) => (
              <div
                key={tabName}
                className={`screening-tab-btn${
                  tab === tabName ? '-selected' : ''
                }`}
                onClick={() => handleTabSwitch(tabName)}
              >
                <div
                  className={`screening-tab-btn-text${
                    tab === tabName ? '-selected' : ''
                  }`}
                >
                  {tabName === 'All'
                    ? `All ${
                        database.type.charAt(0).toUpperCase() +
                        database.type.slice(1)
                      }`
                    : tabName}
                </div>
              </div>
            ),
          )}
        </div>
        <div className='module-header-title-conts'>
          <Tooltip
            title={isScreeningCurrently ? 'Screening in progress' : ''}
            placement='bottom-start'
            arrow
          >
            <span>
              {database.type === 'tariffs' ? (
                <button
                  className='screening-btn'
                  onClick={runCheckForDatabase}
                  disabled={isScreeningCurrently}
                  style={
                    isScreeningCurrently
                      ? { opacity: 0.5, cursor: 'not-allowed' }
                      : undefined
                  }
                >
                  Run Check
                </button>
              ) : (
                <button
                  className='screening-btn'
                  onClick={runScreeningForDatabase}
                  disabled={isScreeningCurrently || isEntityServiceScreeningPending}
                  style={
                    isScreeningCurrently || isEntityServiceScreeningPending
                      ? { opacity: 0.5, cursor: 'not-allowed' }
                      : undefined
                  }
                >
                  Run Screening
                </button>
              )}
            </span>
          </Tooltip>
          {database.type === 'products' && (
            <Tooltip
              title={isScreeningCurrently ? 'Screening in progress' : ''}
              placement='bottom-start'
              arrow
            >
              <span>
                {productTab === 'Products' && (
                  <div
                    className='btn-new-automate'
                    style={{ verticalAlign: 'middle' }}
                    onClick={() => setProductTab('Compliance Dashboard')}
                  >
                    <div>Compliance Dashboard</div>
                  </div>
                )}
                {productTab === 'Compliance Dashboard' && (
                  <div
                    className='btn-new-automate'
                    style={{ verticalAlign: 'middle' }}
                    onClick={() => setProductTab('Products')}
                  >
                    <div>Close</div>
                  </div>
                )}
              </span>
            </Tooltip>
          )}
          <Tooltip title='Database History' placement='bottom-start' arrow>
            <div
              className='audit-btn-star'
              onClick={() => handleTabSwitch('Screening History')}
            >
              <AccessTimeOutlinedIcon
                sx={{
                  fontSize: 20,
                  color:
                    tab === 'Screening History'
                      ? '#4895ef'
                      : 'var(--card-text)',
                }}
              />
            </div>
          </Tooltip>
          <Tooltip title={'Database Settings'} placement='bottom-start' arrow>
            <div
              className='audit-btn-star'
              onClick={() => handleTabSwitch('Screening Settings')}
            >
              <SettingsOutlinedIcon
                sx={{
                  fontSize: 20,
                  color:
                    tab === 'Screening Settings'
                      ? '#4895ef'
                      : 'var(--card-text)',
                }}
              />
            </div>
          </Tooltip>
          {database.type !== 'tariffs' && (
            <Tooltip title='Generated Reports' placement='bottom-start' arrow>
              <div
                className='audit-btn-star'
                onClick={() => handleTabSwitch('Generated Reports')}
              >
                <PictureAsPdfOutlinedIcon
                  sx={{
                    fontSize: 20,
                    color:
                      tab === 'Generated Reports'
                        ? '#4895ef'
                        : 'var(--card-text)',
                  }}
                />
              </div>
            </Tooltip>
          )}
          {/* Build Report — hidden from database toolbar.
          <Tooltip title='Build Report' placement='bottom-start' arrow>
            <div
              className='audit-btn-star'
              onClick={() => handleTabSwitch('Build Report')}
            >
              <SummarizeOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: tab === 'Build Report' ? '#4895ef' : 'var(--card-text)',
                }}
              />
            </div>
          </Tooltip>
          */}
        </div>
      </div>
      {!cur_user.quickcodeOnly &&
        database.type === 'products' &&
        productTab === 'Compliance Dashboard' && <> </>}
      {['All', 'Review Queue', 'Quarantined', 'Barred', 'Cleared'].includes(
        tab,
      ) &&
        !(
          database.type === 'products' && productTab === 'Compliance Dashboard'
        ) && (
          <>
            <div
              className='bdb-db-list-toolbar'
              style={
                database.type === EntitiesType &&
                (showStatusResetBanner ||
                  statusResetFromQuery ||
                  previousStatusRestoreFlags.length > 0)
                  ? {
                      width: '100%',
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      marginTop: '-12px',
                    }
                  : { width: '100%', alignSelf: 'stretch' }
              }
            >
            {database.type === EntitiesType &&
              (showStatusResetBanner ||
                statusResetFromQuery ||
                previousStatusRestoreFlags.length > 0) && (
                <div className='bdb-status-reset-stack'>
                  {showStatusResetBanner && (
                    <StatusResetBanner
                      count={statusResetCount}
                      onReview={handleReviewStatusResets}
                      onDismiss={handleDismissStatusResetBanner}
                      disabled={isScreeningCurrently}
                    />
                  )}
                  {(statusResetFromQuery ||
                    previousStatusRestoreFlags.length > 0) && (
                    <div className='bdb-status-reset-actions-row'>
                      {statusResetFromQuery && (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 10px',
                            borderRadius: '999px',
                            background: 'rgba(245, 158, 11, 0.12)',
                            border: '1px solid rgba(245, 158, 11, 0.28)',
                            color: 'var(--text-primary)',
                            fontSize: '13px',
                            lineHeight: 1,
                          }}
                        >
                          <span>
                            Status reset by screening · {statusResetChipCount} ×
                          </span>
                          <button
                            type='button'
                            onClick={handleClearStatusResetFilter}
                            disabled={isScreeningCurrently}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'transparent',
                              border: 0,
                              color: 'var(--text-secondary)',
                              cursor: isScreeningCurrently
                                ? 'not-allowed'
                                : 'pointer',
                              opacity: isScreeningCurrently ? 0.6 : 1,
                              padding: 0,
                            }}
                            aria-label='Clear status reset filter'
                          >
                            <CloseOutlinedIcon sx={{ fontSize: 16 }} />
                          </button>
                        </div>
                      )}
                      {previousStatusRestoreFlags.map((flag) => (
                        <RestorePreviousByFlagButton
                          key={flag}
                          flag={flag}
                          loading={loadingRestoreByFlag === flag}
                          disabled={
                            isScreeningCurrently ||
                            (loadingRestoreByFlag !== null &&
                              loadingRestoreByFlag !== flag)
                          }
                          onClick={() => requestRestorePreviousByFlag(flag)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            <div className='team-content-header'>
              <div className='team-content-search'>
                <div className='screening-input-field-container'>
                  <input
                    placeholder='Search Values'
                    className='screening-input-field-content'
                    value={localSearchTerm}
                    onChange={(e) => setLocalSearchTerm(e.target.value)}
                  />
                  {database.type === 'products' && (
                    <FormControl sx={{ width: '175px', height: '40px' }}>
                      <CustomSelect
                        value={localHierarchyType}
                        onChange={(e: React.ChangeEvent<{ value: string }>) =>
                          setLocalHierarchyType(e.target.value)
                        }
                        fullWidth
                        IconComponent={KeyboardArrowDownIcon}
                        inputProps={{
                          MenuProps: {
                            MenuListProps: {
                              sx: {
                                backgroundColor: 'var(--bg-colors)',
                                color: 'var(--Text-Main, var(--text-primary))',
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value={'All'}>All</MenuItem>
                        <MenuItem value={'Assembly'}>Assembly</MenuItem>
                        <MenuItem value={'Sub-Assembly'}>Sub-Assembly</MenuItem>
                        <MenuItem value={'Component'}>Component</MenuItem>
                      </CustomSelect>
                    </FormControl>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px',
                  justifyContent: 'flex-end',
                }}
              >
                {database.type === EntitiesType && (
                  <Tooltip title='Filter' placement='bottom-start' arrow>
                    <div
                      className={`audit-btn-star${
                        hasActiveFilters ? ' selected' : ''
                      }`}
                      onClick={() => setShowFilterModal(true)}
                    >
                      <FilterListIcon
                        sx={{
                          fontSize: 20,
                          color: hasActiveFilters
                            ? 'var(--Primary-400, #4895ef)'
                            : 'var(--text-classify)',
                        }}
                      />
                    </div>
                  </Tooltip>
                )}
                <Tooltip title='Update Status' placement='bottom-start' arrow>
                  <div
                    className='audit-btn-star'
                    onClick={() => setShowBulkStatusTransitionModal(true)}
                  >
                    <EditOutlinedIcon
                      sx={{ fontSize: 20, color: 'var(--text-classify)' }}
                    />
                  </div>
                </Tooltip>
                {database.type === EntitiesType &&
                  selectedWithPreviousStatus.length > 0 && (
                    <>
                      <Tooltip
                        title='Restore previous status'
                        placement='bottom-start'
                        arrow
                      >
                        <div
                          className='audit-btn-star'
                          onClick={() => {
                            if (!restorePreviousStatusMutation.isPending) {
                              handleBulkRestorePrevious();
                            }
                          }}
                          style={{
                            opacity: restorePreviousStatusMutation.isPending
                              ? 0.6
                              : 1,
                            cursor: restorePreviousStatusMutation.isPending
                              ? 'not-allowed'
                              : 'pointer',
                          }}
                        >
                          <ReplayOutlinedIcon
                            sx={{ fontSize: 20, color: 'var(--text-classify)' }}
                          />
                        </div>
                      </Tooltip>
                      <Tooltip
                        title='Decline previous status'
                        placement='bottom-start'
                        arrow
                      >
                        <div
                          className='audit-btn-star'
                          onClick={() => {
                            if (!declinePreviousStatusMutation.isPending) {
                              handleBulkDeclinePrevious();
                            }
                          }}
                          style={{
                            opacity: declinePreviousStatusMutation.isPending
                              ? 0.6
                              : 1,
                            cursor: declinePreviousStatusMutation.isPending
                              ? 'not-allowed'
                              : 'pointer',
                          }}
                        >
                          <NotInterestedOutlinedIcon
                            sx={{ fontSize: 20, color: 'var(--text-classify)' }}
                          />
                        </div>
                      </Tooltip>
                    </>
                  )}
                <Tooltip
                  title={
                    database.type === EntitiesType
                      ? 'Remove from Database'
                      : 'Delete Items'
                  }
                  placement='bottom-start'
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        display: 'flex',
                        padding:
                          'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        borderRadius: 'var(--radius-md, 8px)',
                        background: 'var(--Background-200, var(--bg-colors))',
                        maxWidth: '700px',
                        '& .MuiTooltip-arrow': {
                          color: 'var(--Background-200, var(--bg-colors))',
                        },
                      },
                    },
                  }}
                >
                  <div
                    className='audit-btn-star'
                    onClick={() => setIsDeletingItems(true)}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                      fill='none'
                    >
                      <path
                        d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167'
                        stroke='var(--text-classify)'
                        strokeWidth='1.66667'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                </Tooltip>
              </div>
            </div>
            </div>
            <MasterTable
              viewType='Database'
              loading={isLoadingData}
              cols={
                database.type === 'tariffs' ? (
                  <TariffCols
                    showFilters
                    handleCheckboxClick={handleSelectAll}
                    isSelectedItems={
                      selectedItems.length === tableData?.length &&
                      tableData?.length > 0
                    }
                    sortData={handleSort}
                    sortConfig={{ key: sortBy, direction: sortOrder }}
                  />
                ) : database.type === 'products' ? (
                  <ProductColumns
                    showFilters
                    handleCheckboxClick={handleSelectAll}
                    isSelectedItems={
                      selectedItems.length === tableData?.length &&
                      tableData?.length > 0
                    }
                    sortData={handleSort}
                    sortConfig={{ key: sortBy, direction: sortOrder }}
                  />
                ) : (
                  <Columns
                    showFilters
                    handleCheckboxClick={handleSelectAll}
                    isSelectedItems={
                      selectedItems.length === tableData?.length &&
                      tableData?.length > 0
                    }
                    sortData={handleSort}
                    sortConfig={{ key: sortBy, direction: sortOrder }}
                  />
                )
              }
              suppliersData={processedTableData}
              location={fetchLocation(database.type)}
              handleEdit={
                database.type === 'products' ? handleProductEdit : handleEdit
              }
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              isScreeningInProgress={
                isEntityDatabase ? false : isOpensearchInProgress
              }
              aiInProgress={isAiInProgress}
              screeningProgress={screeningProgress}
              screeningFailureWarning={screeningFailureWarning}
              currentPage={page}
              handleDelete={(entity) => {
                setSelectedItems([entity]);
                setIsDeletingItems(true);
              }}
              totalPages={paginationData?.totalPages || 1}
              onPageChange={(event, newPage) =>
                handleUrlChange({ page: newPage })
              }
              customRowComponent={
                database.type === EntitiesType
                  ? ({
                      supplier: rowSupplier,
                      location: rowLocation,
                      selectedItems: rowSelectedItems,
                      setSelectedItems: setRowSelectedItems,
                      aiInProgress: rowAiInProgress,
                    }) => (
                      <Row
                        supplier={rowSupplier}
                        location={rowLocation}
                        viewType='Database'
                        selectedItems={rowSelectedItems}
                        setSelectedItems={setRowSelectedItems}
                        handleEdit={handleEdit}
                        handleDelete={(entity: any) => {
                          setSelectedItems([entity]);
                          setIsDeletingItems(true);
                        }}
                        hasChildren={
                          !isEntityStatusFlatMode &&
                          Boolean(rowSupplier?.has_children)
                        }
                        isExpanded={
                          isEntityStatusFlatMode
                            ? false
                            : isEntityHierarchyContextMode
                            ? Boolean(
                                rowSupplier?._searchMatchedChildren?.length ||
                                  rowSupplier?._contextChildren?.length,
                              )
                            : Boolean(expandedEntityIds[rowSupplier?.entity_id])
                        }
                        onToggleExpand={() =>
                          toggleEntityExpand(rowSupplier?.entity_id)
                        }
                        databaseId={database?.id}
                        isContextParent={
                          !isEntityStatusFlatMode &&
                          Boolean(rowSupplier?._isContextParent)
                        }
                        isSearchMode={isEntityHierarchyContextMode}
                        searchMatchedChildren={
                          rowSupplier?._searchMatchedChildren ||
                          rowSupplier?._contextChildren
                        }
                        expandedEntityIds={expandedEntityIds}
                        onToggleEntityExpand={toggleEntityExpand}
                        aiInProgress={rowAiInProgress}
                        screeningResultId={
                          screeningStatus?.screeningResultId ?? null
                        }
                        screeningInProgress={isOpensearchInProgress}
                        useOutlinedStatusBadge
                      />
                    )
                  : undefined
              }
            />
          </>
        )}

      {tab === 'Screening History' && (
        <>
          <div className='team-content-header'>
            <div className='team-content-search'>
              <div className='screening-input-field-container'>
                <input
                  placeholder='Search activity'
                  className='screening-input-field-content'
                  value={auditSearchInput}
                  onChange={(e) => setAuditSearchInput(e.target.value)}
                />
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                justifyContent: 'flex-end',
              }}
            >
              <Tooltip title='Filter' placement='bottom-start' arrow>
                <div
                  className={`audit-btn-star${
                    hasActiveAuditFilters ? ' selected' : ''
                  }`}
                  onClick={() => setShowAuditFilterModal(true)}
                >
                  <FilterListIcon
                    sx={{
                      fontSize: 20,
                      color: hasActiveAuditFilters
                        ? 'var(--Primary-400, #4895ef)'
                        : 'var(--text-classify)',
                    }}
                  />
                </div>
              </Tooltip>
            </div>
          </div>
          <MasterTable
            cols={<ActivityHistoryColumns />}
            loading={isScreeningHistoryLoading || isAuditLogsLoading}
            suppliersData={currentHistoryItems}
            selectedItems={[]}
            setSelectedItems={() => {
              /* selection is not needed in this view */
            }}
            customRowComponent={(props: any) => (
              <ActivityHistoryRow
                {...props}
                onRetryScreening={(databaseId: string) => {
                  runEntityServiceScreeningMutation
                    .mutateAsync({
                      databaseId: String(databaseId),
                      screenedBy:
                        cur_user?.basics?.email || cur_user?.basics?.id,
                    })
                    .then((response) => {
                      if (response.success) {
                        requestScreeningCheck();
                        return;
                      }
                      enqueueSnackbar(
                        `Failed to run screening: ${response?.result || 'Unknown error'}`,
                        { variant: 'error' },
                      );
                    });
                }}
              />
            )}
            currentPage={historyPage}
            totalPages={totalHistoryPages}
            onPageChange={(event, newPage) => setHistoryPage(newPage)}
          />
          <AuditLogFilterModal
            open={showAuditFilterModal}
            onClose={() => setShowAuditFilterModal(false)}
            availableActions={auditFilterActions}
            noActionsMessage='No actions recorded yet for this database.'
            filters={auditFilters}
            onApply={(nextFilters) => setAuditFilters(nextFilters)}
          />
        </>
      )}

      {tab === 'Screening Settings' &&
        (database.type === 'tariffs' ? (
          <TariffSettings database={database} />
        ) : (
          <ScreeningSettings database={database} />
        ))}

      {tab === 'Generated Reports' && database.type !== 'tariffs' && (
        <div className='screening-settings'>
          <div className='screening-settings-col' style={{ gap: '12px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <h2 style={{ margin: 0 }}>
                <PictureAsPdfOutlinedIcon
                  sx={{ fontSize: 20, color: 'var(--Primary-400, #4895ef)' }}
                />{' '}
                Generated Reports
              </h2>
              <button
                className='screening-btn'
                onClick={() =>
                  fetchResultsReports({
                    silent: true,
                    page: generatedReportsPage,
                  })
                }
                disabled={isRefreshingResultsReports || isLoadingResultsReports}
                style={{
                  opacity:
                    isRefreshingResultsReports || isLoadingResultsReports
                      ? 0.6
                      : 1,
                }}
              >
                {isRefreshingResultsReports ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <RefreshOutlinedIcon sx={{ fontSize: 16 }} />
                )}
                <span style={{ marginLeft: 6 }}>Refresh</span>
              </button>
            </div>
            <p className='subtitle'>
              Reports generated by anyone in your team for this database appear
              here and can be downloaded by all team members.
            </p>
            <MasterTable
              cols={<GeneratedReportsColumns />}
              loading={isLoadingResultsReports}
              suppliersData={resultsReports}
              selectedItems={[]}
              setSelectedItems={() => {
                /* selection is not needed in this view */
              }}
              customRowComponent={({
                supplier,
              }: {
                supplier: ResultsReportRow;
              }) => (
                <GeneratedReportsRow
                  report={supplier}
                  onDownload={handleDownloadExistingReport}
                  isDownloading={isDownloadingReportJobId === supplier?.jobId}
                />
              )}
              currentPage={generatedReportsPage}
              totalPages={generatedReportsTotalPages}
              onPageChange={(_event, newPage) =>
                setGeneratedReportsPage(newPage)
              }
            />
          </div>
        </div>
      )}

      {tab === 'Build Report' && (
        <AIReportBuilder
          embedded
          sourceRunOverride={{
            type: 'bitedb',
            runId: database.id,
            label: database.name,
          }}
        />
      )}

      {/* Modals */}
      <BulkUploadModal
        open={showBulkUploadModal}
        handleClose={() => setShowBulkUploadModal(false)}
        database={database}
        handleRefresh={refreshListAfterModal}
        onDatabaseUploadStarted={() => setIsBulkUploading(true)}
        onDatabaseUploadFailed={() => setIsBulkUploading(false)}
        onDatabaseUploadCompleted={() => setIsBulkUploading(false)}
        onRequestScreeningCheck={requestScreeningCheck}
      />
      {database.type === EntitiesType && (
        <Modal
          open={showBulkLinkEntitiesModal}
          onClose={() => setShowBulkLinkEntitiesModal(false)}
        >
          <div style={{ outline: 'none' }}>
            <BulkLinkEntitiesModal
              open={showBulkLinkEntitiesModal}
              onClose={() => setShowBulkLinkEntitiesModal(false)}
              databaseId={database.id}
            />
          </div>
        </Modal>
      )}
      <AddEntityModal
        open={showAddItemModal}
        handleClose={() => setShowAddItemModal(false)}
        database={database}
        handleRefresh={refreshListAfterModal}
        onEntityAdded={requestScreeningCheck}
        onImportCsv={handleImportDataClick}
        onDuplicatesDetected={handleSingleDuplicatesDetected}
      />
      <AddSingleTariffModal
        open={showAddTariffModal}
        handleClose={() => setShowAddTariffModal(false)}
        database={database}
        handleRefresh={refetchItems}
        onDuplicatesDetected={handleSingleDuplicatesDetected}
      />
      <InsufficientCredits
        teamId={cur_user?.selected_team?.id || ''}
        isOpen={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        type='entity'
        requiredCredits={requiredDatabaseCredits}
      />

      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <DeleteCommonModal
          handleClose={() => setIsDeleteModalOpen(false)}
          handleDelete={handleDeleteDatabase}
          title='Delete Database?'
          paragraph='Once Deleted there is no way to recover this Database or the information saved on it.'
          isLoading={deleteDatabaseMutation.isPending}
        />
      </Modal>
      <Modal open={isDeletingItems} onClose={() => setIsDeletingItems(false)}>
        <DeleteCommonModal
          handleClose={() => setIsDeletingItems(false)}
          handleDelete={() => handleBulkAction('delete')}
          {...(database.type === EntitiesType
            ? {
                title: `Remove ${selectedItems.length} ${
                  selectedItems.length === 1 ? 'entity' : 'entities'
                } from this Database?`,
                paragraph:
                  "Their Screening History on this Database is removed with them. They stay in your team's other Databases.",
                confirmLabel: 'Remove',
                loadingLabel: 'Removing',
              }
            : {
                title: `Delete ${selectedItems.length} items?`,
                paragraph:
                  'Once deleted there is no way to recover these items.',
              })}
          isLoading={
            database.type === EntitiesType
              ? removeDatabaseEntitiesMutation.isPending
              : database.type === 'tariffs'
              ? deleteBulkTariffsMutation.isPending
              : deleteBulkProductsMutation.isPending
          }
        />
      </Modal>
      <DatabaseFilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        statusFilters={statusFilters}
        riskFilters={riskFilters}
        hitCountRanges={hitCountRanges}
        nameStartsWith={nameStartsWith}
        hasNewResults={hasNewResultsFilter}
        lensFilters={lensFilters}
        onApply={handleApplyDatabaseFilters}
      />
      <Modal
        open={isChangingStatus && !showStatusConfirmModal}
        onClose={() => setIsChangingStatus(false)}
      >
        <ChangeStatus
          isChangingStatus={updateDatabaseEntitiesStatusMutation.isPending}
          handleClose={() => setIsChangingStatus(false)}
          changeStatus={(status: any) => handleStatusSelection(status)}
          curStatus={'clear'}
        />
      </Modal>
      <Modal open={showStatusConfirmModal} onClose={closeStatusConfirmation}>
        <ConfirmStatusChangeModal
          count={selectedItems.length}
          status={pendingStatus}
          onCancel={closeStatusConfirmation}
          onConfirm={confirmStatusChange}
          isProcessing={isStatusUpdating}
        />
      </Modal>
      <Modal
        open={Boolean(pendingRestoreByFlag)}
        onClose={closeRestoreByFlagConfirmation}
      >
        <ConfirmStatusChangeModal
          title='Confirm restore'
          confirmLabel='Restore'
          count={pendingRestoreByFlagCount}
          description={pendingRestoreByFlagDescription}
          onCancel={closeRestoreByFlagConfirmation}
          onConfirm={handleRestorePreviousByFlag}
          isProcessing={loadingRestoreByFlag !== null}
          confirmDisabled={pendingRestoreByFlagCount === 0}
        />
      </Modal>
      <Dialog
        className='dialog_box'
        open={duplicateReviewState.open}
        onClose={handleDuplicateDiscard}
        maxWidth='md'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--bg-colors)',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            borderRadius: '0.5rem',
            overflowY: 'visible',
            maxWidth: '800px',
            width: '90%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle
          id='duplicate-review-modal-title'
          className='modal-header-content modal-title'
        >
          Review Potential Duplicates
          <div
            className='audit-add-modal-head-cont-icon'
            onClick={handleDuplicateDiscard}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
                stroke='white'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <DuplicateReviewContent
            duplicates={duplicateReviewState.duplicates}
            onDiscard={handleDuplicateDiscard}
            onReview={handleDuplicateAddToReview}
            isProcessing={createBulkWithoutDuplicateCheckMutation.isPending}
          />
        </DialogContent>
      </Dialog>
      <BulkStatusTransitionModal
        open={showBulkStatusTransitionModal}
        onClose={() => setShowBulkStatusTransitionModal(false)}
        databaseId={database?.id}
        databaseType={database.type}
        selectedItems={selectedItems}
        activeTab={tab}
      />
      <Modal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      >
        <div style={{ outline: 'none' }}>
          <BulkExportModal
            handleClose={() => setIsExportModalOpen(false)}
            fileName={exportFileName}
            setFileName={setExportFileName}
            downloadFormat={exportFormat}
            setDownloadFormat={setExportFormat}
            isDownloading={isExporting}
            databaseType={database.type} // <-- Pass the database type down
            handleDownload={() => {
              if (database.type === 'tariffs') {
                handleExportTariffs();
              } else {
                handleExportData();
              }
            }}
          />
        </div>
      </Modal>
      <DatabaseResultsReportModal
        open={isResultsReportModalOpen}
        onClose={() => {
          setIsResultsReportModalOpen(false);
          if (tab === 'Generated Reports') {
            fetchResultsReports({
              silent: true,
              page: generatedReportsPage,
            });
          }
        }}
        databaseId={database.id}
        databaseName={database.name}
      />
      <EntityChangesReportModal
        open={isChangesReportModalOpen}
        onClose={() => setIsChangesReportModalOpen(false)}
        databaseId={database.id}
        databaseName={database.name}
        // The change-report endpoint operates on DatabaseEntity ids, which the
        // Bite DB list exposes as association_id (rather than the underlying
        // Entity id held in `id`).
        selectedEntityIds={selectedItems
          .map((item) => item.association_id ?? item.database_entity_id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0)}
      />
    </div>
  );
};

export default DatabaseInfo;
